import React, { useEffect, useState } from "react";
import { Loader2, Smile, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

/**
 * ProfileStatusPill — always-visible one-line status on the user profile.
 *
 * Modes:
 *   • Own account   → click to edit inline; save via PATCH /users/me/status
 *   • Other account → read-only, shows their current status
 *
 * Empty state on own account shows a friendly "+ Set your status" affordance
 * so the entry point is discoverable.
 */
export function ProfileStatusPill({ userId, editable }) {
  const { user } = useAuth();
  const me = editable && user && (!userId || userId === user.user_id);
  const targetUserId = userId || user?.user_id;
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [emoji, setEmoji] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!targetUserId) return;
      setLoading(true);
      try {
        const r = await api.get(`/users/${targetUserId}/status`);
        if (!cancelled) { setRow(r.data); setText(r.data.text || ""); setEmoji(r.data.emoji || ""); }
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [targetUserId]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/users/me/status", { text: text.trim() || null, emoji: emoji.trim() || null });
      setRow(r.data);
      setEditing(false);
      toast.success("Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="inline-flex items-center gap-1.5 text-xs text-stone-400"><Loader2 size={12} className="animate-spin"/> Loading…</div>;

  if (editing) {
    return (
      <div className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-3 py-1.5 shadow-sm" data-testid="status-pill-edit">
        <Input
          value={emoji}
          onChange={e => setEmoji(e.target.value.slice(0, 4))}
          className="w-10 h-7 text-center rounded-full border-stone-200 text-sm px-1"
          placeholder="😊"
          data-testid="status-pill-emoji"
        />
        <Input
          value={text}
          onChange={e => setText(e.target.value.slice(0, 60))}
          className="w-56 h-7 rounded-full border-stone-200 text-sm"
          placeholder="Available · On vacation · Free for meetings"
          data-testid="status-pill-text"
          autoFocus
        />
        <button onClick={save} disabled={busy} className="p-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="status-pill-save">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
        </button>
        <button onClick={() => setEditing(false)} className="p-1 rounded-full hover:bg-stone-100" data-testid="status-pill-cancel"><X size={13}/></button>
      </div>
    );
  }

  const hasStatus = row && (row.text || row.emoji);

  if (!hasStatus && !me) return null;
  if (!hasStatus && me) {
    return (
      <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-emerald-700 border border-dashed border-stone-300 hover:border-emerald-500 rounded-full px-3 py-1 transition-colors" data-testid="status-pill-set">
        <Smile size={13}/> Set your status
      </button>
    );
  }

  return (
    <button
      onClick={me ? () => setEditing(true) : undefined}
      disabled={!me}
      className={`inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-3 py-1 ${me ? "hover:bg-emerald-100" : "cursor-default"}`}
      data-testid="status-pill"
    >
      {row.emoji && <span className="text-base leading-none">{row.emoji}</span>}
      <span className="truncate max-w-[240px]">{row.text}</span>
    </button>
  );
}

export default ProfileStatusPill;
